/**
 * Retention review and holds — Task 13.4, requirements 10.1, 11.6, and 15.1–15.5.
 *
 * ## What a hold is for
 *
 * `support:retention` redacts terminal requests after the implemented 365-day default, and it refuses to run
 * while an *active* request is old enough to be a candidate — that would destroy something still being worked.
 * A hold is the escape valve: an attributed, expiring statement that this particular request must survive the
 * next sweep, with a reason somebody wrote down. It is not a delete, not a pause on the whole job, and not
 * permanent; the server caps it at 90 days from now and requires the expiry and the reason together.
 *
 * ## Mirrored validation, and why
 *
 * The 90-day ceiling and the future requirement are enforced by the store, and this checks them before sending
 * for the same reason the transition table is mirrored in `permissions.ts`: an operator typing a date should be
 * told immediately, in the field, rather than after a round trip that returns a field error. The server stays
 * the authority and a stale clock here changes nothing about what it accepts.
 *
 * Recording a hold is manager and admin only, so an agente sees the state and no controls.
 *
 * ## What is deliberately absent
 *
 * No hard delete. The dashboard cannot destroy a support request, by design: retention is a scheduled,
 * reviewable job with its own command, not a button beside a customer's message.
 */
import {
  contentDisplayTimeZone,
  supportContractLimits,
  type StaffRole,
  type SupportRequestDetail,
} from '@apuestapr/contracts';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DateTimeInput,
  Field,
  Textarea,
} from '@apuestapr/ui';
import { useState, type FormEvent, type ReactElement } from 'react';

import { formatPuertoRicoInstant, puertoRicoZoneLabel } from '../format/instants.ts';
import { canSupport } from './permissions.ts';

/** The store's ceiling, mirrored. 90 days from the update instant, and the expiry must be in the future. */
export const maximumRetentionHoldDays = 90;

const dayMilliseconds = 24 * 60 * 60 * 1_000;

export type RetentionHoldProblem = 'missing' | 'past' | 'tooFar';

/**
 * Validates a proposed expiry against the same two rules the store applies.
 *
 * `now` is injected so a test states the instant rather than depending on when it ran.
 */
export const retentionHoldProblem = (
  until: string | null,
  now: Date,
): RetentionHoldProblem | null => {
  if (until === null) return 'missing';
  const parsed = new Date(until);
  if (Number.isNaN(parsed.getTime())) return 'missing';
  const duration = parsed.getTime() - now.getTime();
  if (duration <= 0) return 'past';
  if (duration > maximumRetentionHoldDays * dayMilliseconds) return 'tooFar';
  return null;
};

const holdProblemMessages: Readonly<Record<RetentionHoldProblem, string>> = {
  missing: 'Indica la fecha y hora en que vence la retención.',
  past: 'La fecha de vencimiento debe estar en el futuro.',
  tooFar: `La retención no puede exceder ${String(maximumRetentionHoldDays)} días desde ahora.`,
};

const Instant = ({ instant }: { readonly instant: string }): ReactElement => (
  <span className="grid gap-1">
    <time dateTime={instant}>{formatPuertoRicoInstant(instant)}</time>
    <span className="text-xs text-muted-foreground">{puertoRicoZoneLabel}</span>
  </span>
);

export interface RetentionPanelProps {
  readonly detail: SupportRequestDetail;
  readonly role: StaffRole;
  readonly busy: boolean;
  readonly onRecord: (until: string, reason: string) => void;
  readonly onLift: () => void;
  /** Injected so the 90-day check is deterministic in a test. */
  readonly now?: Date | undefined;
}

export const RetentionPanel = ({
  detail,
  role,
  busy,
  onRecord,
  onLift,
  now,
}: RetentionPanelProps): ReactElement => {
  const [until, setUntil] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<RetentionHoldProblem | null>(null);
  const [reasonMissing, setReasonMissing] = useState(false);

  const mayRecord = canSupport(role, 'recordRetentionHold');
  const hold = detail.retentionHold;

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    // The contract requires the expiry and the reason together, so both are checked before sending.
    const expiryProblem = retentionHoldProblem(until, now ?? new Date());
    const trimmedReason = reason.trim();
    setProblem(expiryProblem);
    setReasonMissing(trimmedReason === '');
    if (expiryProblem !== null || trimmedReason === '' || until === null) return;
    onRecord(until, trimmedReason);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle level={3}>Retención de datos</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {detail.requiresRetentionReview ? (
          <p className="m-0 flex flex-wrap items-center gap-2 rounded-md border-2 border-foreground bg-muted p-3">
            <Badge variant="accent" shape="diamond">
              Revisión pendiente
            </Badge>
            Esta solicitud sigue activa y ya alcanzó la antigüedad de retención. El barrido
            programado no continuará hasta que se resuelva o se registre una retención.
          </p>
        ) : (
          <p className="m-0 text-muted-foreground">Sin revisión de retención pendiente.</p>
        )}

        {hold === null ? (
          <p className="m-0">Sin retención registrada.</p>
        ) : (
          <dl className="grid gap-2 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-semibold">Vence</dt>
              <dd className="m-0">
                <Instant instant={hold.until} />
              </dd>
            </div>
            <div>
              <dt className="text-sm font-semibold">Registrada por</dt>
              <dd className="m-0">{hold.recordedBy.name}</dd>
            </div>
            <div>
              <dt className="text-sm font-semibold">Registrada</dt>
              <dd className="m-0">
                <Instant instant={hold.recordedAt} />
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-sm font-semibold">Motivo</dt>
              <dd className="m-0 whitespace-pre-wrap">{hold.reason}</dd>
            </div>
          </dl>
        )}

        {mayRecord ? (
          <>
            {hold === null ? null : (
              <Button size="sm" variant="outline" disabled={busy} onClick={onLift}>
                Levantar retención
              </Button>
            )}

            <form className="space-y-3" noValidate onSubmit={submit}>
              <Field
                label={hold === null ? 'Registrar una retención' : 'Reemplazar la retención'}
                description={`Vence como máximo ${String(maximumRetentionHoldDays)} días desde ahora. La zona es la de Puerto Rico.`}
                error={problem === null ? undefined : holdProblemMessages[problem]}
              >
                {(control) => (
                  <DateTimeInput
                    {...control}
                    zone={contentDisplayTimeZone}
                    disabled={busy}
                    value={until}
                    onChange={(next) => {
                      setUntil(next);
                      setProblem(null);
                    }}
                  />
                )}
              </Field>

              <Field
                label="Motivo"
                error={reasonMissing ? 'Explica por qué se retiene esta solicitud.' : undefined}
              >
                {(control) => (
                  <Textarea
                    {...control}
                    rows={2}
                    maxLength={supportContractLimits.retentionHoldReason}
                    disabled={busy}
                    value={reason}
                    onChange={(event) => {
                      setReason(event.target.value);
                      setReasonMissing(false);
                    }}
                  />
                )}
              </Field>

              <Button size="sm" type="submit" disabled={busy}>
                Guardar retención
              </Button>
            </form>
          </>
        ) : (
          <p className="m-0 text-muted-foreground">
            Solo un gerente o administrador puede registrar una retención.
          </p>
        )}
      </CardContent>
    </Card>
  );
};
